import { Component } from '@theme/component';
import { bindOverflowCarousel } from '@theme/bt-scroll-carousel';

const DEFAULT_MAX_PRODUCTS = 8;
const CARD_BATCH_LIMIT = 20;

/**
 * Bo+Tee Discover more — Nosto JSON placements in featured-collection
 * carousel chrome. One JSON request, then one Shopify batch for full
 * product-cards (swatches, wishlist, quick-add).
 *
 * @extends {Component}
 */
class BtNostoRecommendations extends Component {
  /** @type {HTMLElement | null} */
  #tablist = null;

  /** @type {import('./bt-scroll-carousel').OverflowCarouselBinding | null} */
  #carousel = null;

  /** @type {Map<string, DocumentFragment>} */
  #cardCache = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.#tablist = this.querySelector('[role="tablist"]');
    this.#hydrateTabsFromPanels();
    this.#carousel = bindOverflowCarousel({
      root: this,
      track: () => this.#activeTrack(),
      prevButton: this.querySelector('[data-bt-fcc-prev]'),
      nextButton: this.querySelector('[data-bt-fcc-next]'),
      cardSelector: '.bt-fcc__card',
      progressVar: '--bt-fcc-progress',
      features: { progress: true, nav: true },
    });
    this.addEventListener('click', this.#onClick);
    this.#tablist?.addEventListener('keydown', this.#onKeydown);
    this.#loadRecommendations();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.#tablist?.removeEventListener('keydown', this.#onKeydown);
    this.#carousel?.disconnect();
    this.#carousel = null;
    this.#tablist = null;
  }

  #hydrateTabsFromPanels() {
    if (!this.#tablist) return;

    const focusWasInTablist = this.#tablist.contains(document.activeElement);
    this.#tablist.replaceChildren();

    const panels = this.#visiblePanels();
    let firstButton = null;
    let index = 0;

    for (const panel of panels) {
      const label = panel.dataset.tabLabel?.trim();
      const tabId = panel.dataset.tabId;
      if (!label || !tabId) continue;

      const isActive = panel.classList.contains('is-active') || (!firstButton && index === 0);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `bt-fcc__tab ${isActive ? 'text-p4-bold color-text-primary is-active' : 'text-p4 color-text-subdued'}`;
      button.id = `BtNosto-tab-${this.dataset.sectionId}-${tabId}`;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      button.setAttribute('aria-controls', panel.id);
      button.tabIndex = isActive ? 0 : -1;
      button.dataset.tabId = tabId;
      button.textContent = label;
      this.#tablist.appendChild(button);

      if (isActive) {
        firstButton = button;
        panel.classList.add('is-active');
        panel.hidden = false;
      } else {
        panel.classList.remove('is-active');
        panel.hidden = true;
      }
      index += 1;
    }

    const showTabs = this.#tablist.children.length > 1;
    this.classList.toggle('bt-fcc--tabs', showTabs);
    this.#tablist.hidden = !showTabs;
    this.#syncTabpanelRoles(showTabs);

    if (focusWasInTablist && showTabs) {
      firstButton?.focus();
    }
  }

  /**
   * Tabpanels without a visible tablist are announced as orphan panels.
   * @param {boolean} showTabs
   */
  #syncTabpanelRoles(showTabs) {
    for (const panel of this.#panels()) {
      const tab = this.#tabs().find((item) => item.dataset.tabId === panel.dataset.tabId);
      if (showTabs && tab && !panel.hasAttribute('data-bt-nosto-empty')) {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
      } else {
        panel.removeAttribute('role');
        panel.removeAttribute('aria-labelledby');
      }
    }
  }

  /**
   * @returns {HTMLElement[]}
   */
  #panels() {
    return Array.from(this.querySelectorAll('.bt-fcc__panel')).filter((el) => el instanceof HTMLElement);
  }

  /**
   * @returns {HTMLElement[]}
   */
  #visiblePanels() {
    return this.#panels().filter((panel) => !panel.hasAttribute('data-bt-nosto-empty'));
  }

  /**
   * @returns {HTMLElement | null}
   */
  #activeTrack() {
    const panel = this.querySelector('.bt-fcc__panel.is-active:not([hidden])') || this.querySelector('.bt-fcc__panel:not([hidden])');
    if (panel instanceof HTMLElement) {
      const track = panel.querySelector('[data-bt-fcc-track]');
      if (track instanceof HTMLElement) return track;
    }
    const fallback = this.querySelector('[data-bt-fcc-track]');
    return fallback instanceof HTMLElement ? fallback : null;
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const tab = target.closest('[role="tab"]');
    if (!(tab instanceof HTMLElement) || !this.#tablist?.contains(tab)) return;
    this.#activate(tab, { focus: false });
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    const tabs = this.#tabs();
    if (!tabs.length) return;

    const current = document.activeElement;
    if (!(current instanceof HTMLElement) || current.getAttribute('role') !== 'tab') return;

    const index = tabs.indexOf(current);
    if (index < 0) return;

    let nextIndex = -1;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (index + 1) % tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activate(tabs[nextIndex], { focus: true });
  };

  /**
   * @returns {HTMLElement[]}
   */
  #tabs() {
    if (!this.#tablist) return [];
    return Array.from(this.#tablist.querySelectorAll('[role="tab"]')).filter((el) => el instanceof HTMLElement);
  }

  /**
   * @param {HTMLElement} tab
   * @param {{ focus?: boolean }} [options]
   */
  #activate(tab, options = {}) {
    const tabId = tab.dataset.tabId;
    if (!tabId) return;

    for (const other of this.#tabs()) {
      const selected = other === tab;
      other.classList.toggle('is-active', selected);
      other.classList.toggle('text-p4-bold', selected);
      other.classList.toggle('color-text-primary', selected);
      other.classList.toggle('text-p4', !selected);
      other.classList.toggle('color-text-subdued', !selected);
      other.setAttribute('aria-selected', selected ? 'true' : 'false');
      other.tabIndex = selected ? 0 : -1;
    }

    for (const panel of this.#panels()) {
      const active = panel.dataset.tabId === tabId;
      panel.classList.toggle('is-active', active);
      panel.hidden = !active || panel.hasAttribute('data-bt-nosto-empty');
    }

    this.#carousel?.update();

    if (options.focus) {
      tab.focus();
    }
  }

  async #loadRecommendations() {
    const panels = this.#panels().filter((panel) => panel.dataset.nostoPlacement);
    if (!panels.length) {
      this.#onEmpty();
      return;
    }

    try {
      const placementIds = panels.map((panel) => panel.dataset.nostoPlacement || '').filter(Boolean);
      const response = await this.#recommendationsResponse(placementIds);
      if (!response) {
        this.#onEmpty();
        return;
      }
      const recommendations = response.recommendations || {};
      const limit = parseMaxProducts(this.dataset.maxProducts);
      /** @type {{ panel: HTMLElement, campaign: any, products: NostoRecProduct[] }[]} */
      const filledPlans = [];
      const handles = [];

      for (const panel of panels) {
        const placementId = panel.dataset.nostoPlacement || '';
        const campaign = recommendations[placementId];
        const products = campaignProducts(campaign).slice(0, limit);
        filledPlans.push({ panel, campaign, products });
        for (const product of products) {
          const handle = productHandle(product);
          if (handle) handles.push(handle);
        }
      }

      await this.#prefetchCards(handles);
      let filled = 0;

      for (const { panel, campaign, products } of filledPlans) {
        const ok = this.#fillPanel(panel, products);
        if (ok) {
          filled += 1;
          if (campaign && typeof campaign === 'object') {
            const track = panel.querySelector('[data-bt-fcc-track]');
            if (track instanceof HTMLElement) this.#attributeClicks(track, campaign);
          }
        } else {
          this.#markEmpty(panel);
        }
      }

      if (!filled) {
        this.#onEmpty();
        return;
      }

      this.hidden = false;
      this.#setReady();
      this.#activateFirstFilled();
      this.#hydrateTabsFromPanels();
      this.#carousel?.update();
    } catch (error) {
      console.warn('[bt-nosto-recommendations] Nosto recommendations failed', error);
      this.#onEmpty();
    }
  }

  /**
   * Load every visible placement in one request through the app-embed client.
   * @param {string[]} placementIds
   */
  async #recommendationsResponse(placementIds) {
    const api = await getNostoApi();
    if (!api) return null;
    return this.#fetchRecommendations(api, placementIds);
  }

  /**
   * @param {HTMLElement} track
   * @param {any} campaign
   */
  #attributeClicks(track, campaign) {
    const nostojs = window.nostojs;
    if (typeof nostojs !== 'function') return;
    nostojs((api) => {
      if (typeof api.attributeProductClicksInCampaign === 'function') {
        api.attributeProductClicksInCampaign(track, campaign);
      }
    });
  }

  /**
   * @param {any} api
   * @param {string[]} placementIds
   */
  async #fetchRecommendations(api, placementIds) {
    if (!hasTaggedProducts(api) && this.dataset.productId && typeof api.defaultSession === 'function') {
      return api
        .defaultSession()
        .setResponseMode('JSON_ORIGINAL')
        .viewProduct(this.dataset.productId)
        .setPlacements(placementIds)
        .load();
    }

    return api
      .createRecommendationRequest({ includeTagging: true })
      .setResponseMode('JSON_ORIGINAL')
      .setElements(placementIds)
      .load();
  }

  /**
   * @param {string[]} handles
   */
  async #prefetchCards(handles) {
    const unique = [...new Set(handles.filter(Boolean))].slice(0, CARD_BATCH_LIMIT);
    const missing = unique.filter((handle) => !this.#cardCache.has(handle));
    if (!missing.length) return;

    try {
      await this.#fetchCardBatch(missing);
    } catch (error) {
      console.warn('[bt-nosto-recommendations] Batch card fetch failed, using per-product fallback', error);
    }

    const stillMissing = missing.filter((handle) => !this.#cardCache.has(handle));
    if (stillMissing.length) {
      await Promise.all(stillMissing.map((handle) => this.#fetchCard(handle)));
    }
  }

  /**
   * @param {string[]} handles
   */
  async #fetchCardBatch(handles) {
    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}search?q=${encodeURIComponent(handles.join(','))}&type=product&view=bt-product-cards`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Card batch failed: ${response.status}`);

    const html = await response.text();
    this.#ingestCardHtml(html);
  }

  /**
   * @param {string} html
   */
  #ingestCardHtml(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    for (const wrap of doc.querySelectorAll('[data-product-handle]')) {
      if (!(wrap instanceof HTMLElement)) continue;
      const handle = wrap.dataset.productHandle;
      const card = wrap.querySelector('product-card');
      if (!handle || !card) continue;
      this.#storeCard(handle, card);
    }
  }

  /**
   * @param {string} handle
   */
  async #fetchCard(handle) {
    if (this.#cardCache.has(handle)) return;

    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}products/${encodeURIComponent(handle)}?view=collection-card`;
    const response = await fetch(url);
    if (!response.ok) return;

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const card = doc.querySelector('product-card');
    if (!card) return;
    this.#storeCard(handle, card);
  }

  /**
   * @param {string} handle
   * @param {Element} card
   */
  #storeCard(handle, card) {
    const fragment = document.createDocumentFragment();
    fragment.append(card.cloneNode(true));
    this.#cardCache.set(handle, fragment);
  }

  /**
   * @param {HTMLElement} panel
   * @param {NostoRecProduct[]} products
   * @returns {boolean}
   */
  #fillPanel(panel, products) {
    const track = panel.querySelector('[data-bt-fcc-track]');
    if (!(track instanceof HTMLElement)) return false;

    const cards = products.map((product) => this.#cardFor(product)).filter(Boolean);
    if (!cards.length) return false;

    track.replaceChildren(...cards);
    panel.removeAttribute('data-bt-nosto-empty');
    return true;
  }

  /**
   * @param {NostoRecProduct} product
   * @returns {HTMLElement | null}
   */
  #cardFor(product) {
    const handle = productHandle(product);
    if (!handle) return null;

    const cached = this.#cardCache.get(handle);
    const card = cached?.firstElementChild ? cached.firstElementChild.cloneNode(true) : null;
    if (!(card instanceof Element)) return null;

    const wrap = document.createElement('div');
    uniquifyDomIds(card);
    wrap.className = 'bt-fcc__card';
    wrap.dataset.productId = String(product.product_id || product.productId || '');
    wrap.dataset.productHandle = handle;
    wrap.append(card);
    return wrap;
  }

  /**
   * @param {HTMLElement} panel
   */
  #markEmpty(panel) {
    panel.setAttribute('data-bt-nosto-empty', '');
    panel.hidden = true;
    panel.classList.remove('is-active');
  }

  #activateFirstFilled() {
    const filled = this.#visiblePanels();
    filled.forEach((panel, index) => {
      const active = index === 0;
      panel.classList.toggle('is-active', active);
      panel.hidden = !active;
    });
  }

  #onEmpty() {
    this.#setReady();

    if (this.#keepPlaceholders() || !this.hasAttribute('data-hide-when-empty')) {
      this.#hydrateTabsFromPanels();
      this.#carousel?.update();
      return;
    }

    for (const panel of this.#panels()) {
      this.#markEmpty(panel);
    }
    this.#hydrateTabsFromPanels();
    this.hidden = true;
  }

  #setReady() {
    this.removeAttribute('aria-busy');
    const status = this.querySelector('[data-bt-nosto-status]');
    if (!(status instanceof HTMLElement)) return;
    status.textContent = '';
    status.hidden = true;
  }

  #keepPlaceholders() {
    return Boolean(window.Shopify?.designMode);
  }
}

/**
 * Keep the shared Nosto client lazy on product pages that do not render this
 * section.
 * @returns {Promise<any | null>}
 */
function getNostoApi() {
  return import('@theme/bt-nosto-search').then((mod) =>
    mod.getNostoApi(undefined, { requireSearch: false })
  );
}

/**
 * @param {any} api
 */
function hasTaggedProducts(api) {
  try {
    const products = api.pageTagging?.()?.products;
    return Array.isArray(products) && products.length > 0;
  } catch {
    return false;
  }
}

/**
 * @param {any} campaign
 * @returns {NostoRecProduct[]}
 */
function campaignProducts(campaign) {
  if (!campaign || typeof campaign === 'string') return [];
  if (Array.isArray(campaign.products)) return campaign.products;
  if (Array.isArray(campaign)) return campaign;
  return [];
}

/**
 * @param {string | undefined} value
 */
function parseMaxProducts(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : DEFAULT_MAX_PRODUCTS;
}

/**
 * @typedef {object} NostoRecProduct
 * @property {string} [handle]
 * @property {string} [url]
 * @property {string} [name]
 * @property {string} [title]
 * @property {string} [product_id]
 * @property {string} [productId]
 */

/**
 * @param {NostoRecProduct} product
 */
function productHandle(product) {
  if (product.handle) return product.handle.replace(/^\/+|\/+$/g, '');
  return handleFromProductUrl(product.url);
}

let clonedCardUid = 0;

/**
 * Cloned product-cards reuse Liquid ids. Suffix them so two tabs (or a
 * PDP card plus a rec card) do not share ids in the accessibility tree.
 * @param {Element} root
 */
function uniquifyDomIds(root) {
  const suffix = `-${++clonedCardUid}`;
  /** @type {Map<string, string>} */
  const remap = new Map();

  /** @param {Element} el */
  const apply = (el) => {
    if (!el.id) return;
    const next = `${el.id}${suffix}`;
    remap.set(el.id, next);
    el.id = next;
  };

  apply(root);
  root.querySelectorAll('[id]').forEach((el) => apply(el));
  if (!remap.size) return;

  /** @param {Element} el @param {string} attr */
  const rewrite = (el, attr) => {
    const value = el.getAttribute(attr);
    if (!value) return;
    el.setAttribute(
      attr,
      value
        .split(/\s+/)
        .map((id) => remap.get(id) || id)
        .join(' ')
    );
  };

  const scoped = [root, ...root.querySelectorAll('[aria-labelledby], [aria-describedby], [aria-controls], [for]')];
  for (const el of scoped) {
    rewrite(el, 'aria-labelledby');
    rewrite(el, 'aria-describedby');
    rewrite(el, 'aria-controls');
    rewrite(el, 'for');
  }
}

/**
 * @param {string} [url]
 */
function handleFromProductUrl(url) {
  if (!url) return '';
  try {
    const path = new URL(url, window.location.origin).pathname;
    const match = path.match(/\/products\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  } catch {
    return '';
  }
}

if (!customElements.get('bt-nosto-recommendations')) {
  customElements.define('bt-nosto-recommendations', BtNostoRecommendations);
}
